import * as api from "./api.js";
import { byId, defOf, iconOf } from "./data.js";
import { genLevel } from "./level.js";
import { currentLoadout, invalidateBoards, renderLobby, setBest } from "./lobby.js";
import { modeOf } from "./modes.js";
import { runLevel } from "./raid.js";
import {
  bagCap, foundGear, loadBest, loadOf, mergeBest, newRun, runValue, saveBest, settledValue,
} from "./run.js";
import type { SettlementOutcome } from "./run.js";
import { paintIcons, qVar, slotHtml } from "./ui.js";
import type {
  ItemId, ItemInstance, LevelResult, Loadout, RunState, ThemeId,
} from "./core/types.js";

const appElement = document.getElementById("app");
if (!appElement) throw new Error("Missing #app");
const app: HTMLElement = appElement;

let best = loadBest();
let run: RunState = newRun();
let mode = modeOf("challenge");
let themeId: ThemeId | null = null;
let brought: Loadout | null = null;

/* ---------- 大厅 ---------- */
async function home() {
  await renderLobby(app, deploy, best);
  if (!best.seenTip) showTip();
}

/* ---------- 一局的推进 ---------- */
async function deploy(modeId: string, theme: ThemeId | null): Promise<void> {
  mode = modeOf(modeId);
  themeId = theme;

  // 带装备的模式要先跟服务端结账，拿回它认可的那份配装；
  // 没登录或连不上就是裸装，游戏照常开。
  let loadout = null;
  if (mode.loadout && api.currentUser()) {
    const picked = currentLoadout();
    try {
      loadout = await api.startRun(mode.id, picked.equip, picked.carry);
    } catch (caught) {
      return showError("出击失败", caught instanceof Error ? caught.message : "未知错误");
    }
  }

  run = newRun(loadout);
  brought = loadout;
  nextLevel();
}

function nextLevel(): void {
  try {
    runLevel(run, app, genLevel(run.depth, themeId), onLevelDone);
  } catch (err) {
    showCrash(err);
  }
}

function onLevelDone(r: LevelResult): void {
  if (!r.ok) {
    void finish(r, "dead");
    return;
  }
  // 通关回血，平静期内解决掉的给得更多：把「快点走」变成一条有实际收益的打法，
  // 而不是嘴上说说的建议
  const heal = r.alarm ? 15 : 30;
  run.hp = Math.min(100, run.hp + heal);
  // 地狱模式有终点：撑过最后一层是通关，而不是「再来一层」
  if (mode.layers && r.depth >= mode.layers) {
    void finish(r, "clear");
    return;
  }
  showLevelClear(r, heal);
}

type RunOutcome = SettlementOutcome;

// 一局结束的唯一出口：本地记录、上传成绩、给金币，都在这里收口
async function finish(r: LevelResult, outcome: RunOutcome): Promise<void> {
  // 背包里的 loot 只是未兑现价值。死亡时整包归零，但仓库、装备记录不受影响。
  // 只有主动撤离或打穿十八层，价值才真正属于玩家。
  const carriedValue = runValue(run);
  const value = settledValue(run, outcome);
  const rec = {
    depth: run.depth > best.depth,
    kills: run.kills > best.kills,
    value: value > best.value,
  };
  const scoredRun = outcome === "dead"
    ? { ...run, bag: run.bag.filter((item) => defOf(item).cat !== "loot") }
    : run;
  best = mergeBest(best, scoredRun);
  setBest(best);

  let earned = null;
  let stashed: ItemId[] = [];
  if (api.currentUser()) {
    try {
      const posted = await api.submitScore({
        mode: mode.id,
        theme: themeId,
        depth: run.depth,
        kills: run.kills,
        value,
        duration: Math.floor(run.time),
        outcome,
        found: outcome === "dead" ? undefined : foundGear(run, brought),
      });
      earned = posted.earned;
      stashed = posted.stashed;
      invalidateBoards();
    } catch {
      /* 成绩没交上去不该挡着玩家看结算，本地记录已经存了 */
    }
  }
  showResult(r, { outcome, value, lostValue: carriedValue - value, rec, earned, stashed });
}

const lootList = (items: readonly ItemInstance[]): string => {
  const loot = items.filter((it) => defOf(it).cat === "loot");
  if (!loot.length) return `<p class="hint">背包里没有战利品，这一局的分数是 0。</p>`;
  return `<div class="loot-list">${loot
    .map((it) => {
      const d = defOf(it);
      return `<div class="loot-row" style="--q:${qVar(d.value)}">
        <canvas data-ico="${iconOf(d)}" data-s="26"></canvas>
        <span class="nm">${d.name}${d.risk ? `<small class="loot-risk">${d.risk}</small>` : ""}</span>
        <span class="vol">${d.size || 1} 格</span>
        <span class="px">${d.value}</span></div>`;
    })
    .join("")}</div>`;
};

const modal = (html: string): HTMLDivElement => {
  const box = document.createElement("div");
  box.className = "modal";
  box.innerHTML = `<div class="dialog">${html}</div>`;
  document.body.append(box);
  paintIcons(box);
  return box;
};

function showLevelClear(r: LevelResult, heal: number): void {
  const nextDepth = r.depth + 1;
  const gear = (["weapon", "armor", "helmet", "bag"] as const)
    .map((slot) => slotHtml(run.equip[slot])).join("");
  const box = modal(`
    <h3 class="ok">${r.theme ? `${r.theme} · ` : ""}第 ${r.depth} 层已脱离</h3>
    <div class="result-grid">
      <div class="result-cell"><span class="eyebrow">本层击杀</span><b>${r.kills}</b></div>
      <div class="result-cell"><span class="eyebrow">本层耗时</span><b>${Math.floor(r.time)}s</b></div>
      <div class="result-cell"><span class="eyebrow">战利品</span>
        <b style="color:var(--c-amber)">${runValue(run)}</b></div>
    </div>
    <p class="hint">${r.alarm
      ? `拖到了警报之后，只恢复 <b>${heal}</b> 点生命。`
      : `平静期内脱离，恢复 <b style="color:var(--c-ok)">${heal}</b> 点生命。`}
      当前 <b>${Math.round(run.hp)}</b> / 100。
      ${mode.layers ? `还剩 <b>${mode.layers - r.depth}</b> 层到底。` : ""}</p>
    <p class="hint"><b style="color:var(--c-amber)">背包里的 ${runValue(run)} 分尚未兑现。</b>
      现在撤离会全部封存；继续下潜，死亡会失去全部未兑现战利品，但仓库装备不受影响。</p>
    <span class="eyebrow">当前装备</span>
    <div class="slot-grid" style="margin:8px 0 14px">${gear}</div>
    <span class="eyebrow">背包 ${loadOf(run.bag)} / ${bagCap(run)} 格</span>
    ${lootList(run.bag)}
    <div class="foot">
      <button class="btn btn-primary" type="button" id="extract">带货撤离 · 兑现 ${runValue(run)}</button>
      <button class="btn btn-danger" type="button" id="next">继续下潜到第 ${nextDepth} 层</button>
    </div>`);
  box.querySelector<HTMLButtonElement>("#extract")?.addEventListener("click", () => {
    box.remove();
    void finish(r, "extract");
  });
  box.querySelector<HTMLButtonElement>("#next")?.addEventListener("click", () => {
    box.remove();
    run.depth = nextDepth;
    nextLevel();
  });
}

interface ResultSummary {
  outcome: RunOutcome;
  value: number;
  lostValue: number;
  rec: { depth: boolean; kills: boolean; value: boolean };
  earned: number | null;
  stashed: ItemId[];
}

function showResult(r: LevelResult, { outcome, value, lostValue, rec, earned, stashed }: ResultSummary): void {
  const success = outcome !== "dead";
  const badge = (on: boolean): string => on ? `<span class="tag tag-legend rec">新纪录</span>` : "";
  const box = modal(`
    <h3 class="${success ? "ok" : "bad"}">${outcome === "clear"
      ? `打穿了 ${mode.layers} 层`
      : outcome === "extract"
        ? `带货撤离 · 第 ${r.depth} 层`
        : `倒在第 ${r.depth} 层`}</h3>
    <div class="result-grid">
      <div class="result-cell"><span class="eyebrow">深度 ${badge(rec.depth)}</span>
        <b>${run.depth}<small> 层</small></b></div>
      <div class="result-cell"><span class="eyebrow">击杀 ${badge(rec.kills)}</span>
        <b>${run.kills}</b></div>
      <div class="result-cell"><span class="eyebrow">战利品 ${badge(rec.value)}</span>
        <b style="color:var(--c-amber)">${value}</b></div>
    </div>
    <p class="hint">总耗时 ${Math.floor(run.time)}s。${earned === null
      ? "成绩已存在本机。登录之后才会上榜，也才有金币和仓库。"
      : `战利品和下潜津贴折成 <b class="gold">${earned}</b> 金币，已存进账号。`}</p>
    ${stashed.length
      ? `<p class="hint">缴获已入仓：<b>${stashed.map((id) => byId(id).name).join("、")}</b>。下次主题挑战可以从装备页带下去。</p>`
      : ""}
    ${lostValue > 0
      ? `<p class="hint"><b class="bad">死亡使 ${lostValue} 分未兑现战利品全部遗失。</b>
          仓库装备没有损失。</p>`
      : ""}
    <span class="eyebrow">${outcome === "dead" ? "遗失的背包" : "已兑现背包"}</span>
    ${lootList(run.bag)}
    <div class="foot">
      <button class="btn btn-primary" type="button" id="again">再来一局</button>
      <button class="btn btn-ghost" type="button" id="home">回大厅</button>
    </div>`);
  box.querySelector<HTMLButtonElement>("#again")?.addEventListener("click", () => {
    box.remove();
    deploy(mode.id, themeId);
  });
  box.querySelector<HTMLButtonElement>("#home")?.addEventListener("click", () => {
    box.remove();
    home();
  });
}

/* ---------- 兜底 ---------- */
function showError(title: string, detail: string): void {
  const box = modal(`
    <h3 class="bad">${title}</h3>
    <p class="hint">${detail}</p>
    <div class="foot"><button class="btn btn-primary btn-block" type="button" id="back">回大厅</button></div>`);
  box.querySelector<HTMLButtonElement>("#back")?.addEventListener("click", () => {
    box.remove();
    home();
  });
}

function showCrash(err: unknown): void {
  const box = modal(`
    <h3 class="bad">出错了</h3>
    <pre class="hint" style="white-space:pre-wrap;max-height:40vh;overflow:auto">${
      String(err instanceof Error ? err.stack || err.message : err)
    }</pre>
    <div class="foot"><button class="btn btn-primary btn-block" type="button" id="back">回大厅</button></div>`);
  box.querySelector<HTMLButtonElement>("#back")?.addEventListener("click", () => {
    box.remove();
    home();
  });
}

window.addEventListener("error", (e) => showCrash(e.error || e.message));

function showTip(): void {
  if (document.querySelector(".modal[data-tip]")) return;
  const box = modal(`
    <h3>怎么玩</h3>
    <ul>
      <li><b>目标是往下走。</b>摸到绿色出口读条 3 秒，就进下一层。可以一个敌人都不杀。</li>
      <li><b>场景会改变规则。</b>古墓有隐蔽尖刺，地宫能献血换宝，实验室的污染区会持续伤害。</li>
      <li><b>箱子给分，敌人给装备。</b>只搜箱子能刷分但会一直很弱；打掉敌人才能换枪换甲。</li>
      <li><b>背包里的战利品还不属于你。</b>每层结束可以带货撤离并兑现，或继续下潜。
        如果死在后面，全部未兑现战利品都会丢失；仓库装备不会丢。</li>
      <li><b>掠夺者也在搜刮，他们是来抢的，不是来等你的。</b>他们会开箱子、装进自己包里，
        搜满就往出口跑，跑掉的东西这一层再也拿不到。<b>头顶的琥珀小点是他背了几件</b>：
        空手的白打，养肥了再宰，一具尸体顶好几个箱子。</li>
      <li><b>掠夺者会开枪还击</b>，也只有他们身上有<b>保险箱钥匙</b>——最值钱的箱子得靠打架才能开。</li>
      <li><b>声音会改变战局。</b>枪声、保险箱和高价值物会引来附近敌人；
        普通怪也会攻击开枪的掠夺者，你可以让他们互相消耗。
        ${"按 G 或在背包里点战利品，可朝瞄准方向掷出诱饵，声音落在落点。"}</li>
      <li><b>掠夺者跑出口也要读条。</b>被打会中断。带着货跑掉的人会在后续层以逃犯身份再现。</li>
      <li><b>警报是你自己打出来的。</b>HUD 上的<b>隐蔽</b>掉光就会拉警报，敌人开始不断增援。
        静默推进掉得很慢，每开一枪都会大幅拉低它，近战几乎不出声。</li>
      <li><b>背包按体积算</b>，装不下就得丢；而且<b>装得越满跑得越慢</b>。
        金条额外压身，显卡移动时会响，数据核心会周期性广播位置。</li>
      <li>死了不损失仓库装备，但会失去本局尚未兑现的全部战利品。</li>
    </ul>
    <div class="foot"><button class="btn btn-primary btn-block" type="button" id="ok">知道了</button></div>`);
  box.dataset.tip = "1";
  box.querySelector<HTMLButtonElement>("#ok")?.addEventListener("click", () => {
    best.seenTip = true;
    saveBest(best);
    box.remove();
  });
}

api.boot().then(home, home);
